import { keccak256, toUtf8Bytes } from "ethers";
import type { V2KeyContext, V2Keys } from "./keys";
import type { RecoveredNote } from "./recovery";

export type PendingOnboarding = {
  identity: string;
  kind: "registration" | "subwallet";
  commitment: string;
  subwallet?: string;
  txHash?: string;
  /** Public, replay-safe registration payload; never decrypted notes or keys. */
  registrationRequest?: {
    register_control: {
      commitment: string;
      encrypted_note: string;
      proof: string;
    };
  };
  /** Public creation payload permits exact receipt matching after reload. */
  creationRequest?: {
    create_subwallet: {
      root: string;
      nullifier: string;
      output_commitment: string;
      encrypted_output: string;
      salt: string;
      expiry_height: number;
      proof: string;
    };
  };
  creationRelayer?: string;
  registrationCommitment?: string;
};

/** Preserve ambiguous registration across reloads without retaining secrets. */
export class PendingOnboardingRegistry {
  private readonly memory = new Map<string, PendingOnboarding>();
  private storage(): Storage | undefined {
    try {
      return globalThis.localStorage;
    } catch {
      return undefined;
    }
  }
  private key(identity: string): string {
    return `redacted:v2:pending-onboarding:${this.binding(identity)}`;
  }
  private binding(identity: string): string {
    // Do not persist the public-wallet/account identity beside its commitment.
    // The recovery-derived ID in the identity also prevents an address-only
    // dictionary lookup against this domain-separated local binding.
    return keccak256(
      toUtf8Bytes(`redacted:v2:pending-onboarding:v1\0${identity}`)
    ).slice(2);
  }
  get(identity: string): PendingOnboarding | undefined {
    const current = this.memory.get(identity);
    if (current) return current;
    let raw: string | null | undefined;
    try {
      raw = this.storage()?.getItem(this.key(identity));
    } catch {
      return undefined;
    }
    if (raw == null) return undefined;
    const invalid = () =>
      new Error(
        "Account setup status could not be read. Keep your browser data and try again."
      );
    try {
      if (raw.length > 200_000) throw invalid();
      const value = JSON.parse(raw) as Omit<PendingOnboarding, "identity"> & {
        binding: string;
      };
      if (
        value.binding !== this.binding(identity) ||
        !["registration", "subwallet"].includes(value.kind) ||
        !/^[0-9a-f]{64}$/.test(value.commitment) ||
        (value.kind === "subwallet" && typeof value.subwallet !== "string")
      )
        throw invalid();
      if (value.registrationRequest) {
        const request = value.registrationRequest.register_control;
        if (
          value.kind !== "registration" ||
          Object.keys(value.registrationRequest).join() !==
            "register_control" ||
          !request ||
          request.commitment !== value.commitment ||
          typeof request.encrypted_note !== "string" ||
          typeof request.proof !== "string" ||
          Object.keys(request).sort().join() !==
            "commitment,encrypted_note,proof"
        )
          throw invalid();
      }
      if (value.creationRequest) {
        const request = value.creationRequest.create_subwallet;
        if (
          value.kind !== "subwallet" ||
          typeof value.creationRelayer !== "string" ||
          !/^[0-9a-f]{64}$/.test(value.registrationCommitment ?? "") ||
          Object.keys(value.creationRequest).join() !== "create_subwallet" ||
          !request ||
          request.output_commitment !== value.commitment ||
          !Number.isSafeInteger(request.expiry_height) ||
          request.expiry_height < 1 ||
          [
            request.root,
            request.nullifier,
            request.encrypted_output,
            request.salt,
            request.proof,
          ].some((field) => typeof field !== "string") ||
          Object.keys(request).sort().join() !==
            "encrypted_output,expiry_height,nullifier,output_commitment,proof,root,salt"
        )
          throw invalid();
      }
      const pending: PendingOnboarding = {
        identity,
        kind: value.kind,
        commitment: value.commitment,
        ...(value.subwallet && { subwallet: value.subwallet }),
        ...(value.txHash && { txHash: value.txHash }),
        ...(value.registrationRequest && {
          registrationRequest: value.registrationRequest,
        }),
        ...(value.creationRequest && {
          creationRequest: value.creationRequest,
          creationRelayer: value.creationRelayer,
          registrationCommitment: value.registrationCommitment,
        }),
      };
      this.memory.set(identity, pending);
      return pending;
    } catch {
      // A damaged journal is not evidence that registration never happened.
      throw invalid();
    }
  }
  set(identity: string, pending: PendingOnboarding): void {
    if (pending.identity !== identity)
      throw new Error("Private account changed while onboarding");
    const storage = this.storage();
    const binding = this.binding(identity);
    try {
      // Explicitly whitelist public fields; callers cannot persist note keys.
      const value = {
        binding,
        kind: pending.kind,
        commitment: pending.commitment,
        ...(pending.subwallet && { subwallet: pending.subwallet }),
        ...(pending.txHash && { txHash: pending.txHash }),
        ...(pending.registrationRequest && {
          registrationRequest: {
            register_control: {
              commitment:
                pending.registrationRequest.register_control.commitment,
              encrypted_note:
                pending.registrationRequest.register_control.encrypted_note,
              proof: pending.registrationRequest.register_control.proof,
            },
          },
        }),
        ...(pending.creationRequest && {
          creationRelayer: pending.creationRelayer,
          registrationCommitment: pending.registrationCommitment,
          creationRequest: {
            create_subwallet: {
              root: pending.creationRequest.create_subwallet.root,
              nullifier: pending.creationRequest.create_subwallet.nullifier,
              output_commitment:
                pending.creationRequest.create_subwallet.output_commitment,
              encrypted_output:
                pending.creationRequest.create_subwallet.encrypted_output,
              salt: pending.creationRequest.create_subwallet.salt,
              expiry_height:
                pending.creationRequest.create_subwallet.expiry_height,
              proof: pending.creationRequest.create_subwallet.proof,
            },
          },
        }),
      };
      if (!storage) throw new Error("Browser storage unavailable");
      storage.setItem(this.key(identity), JSON.stringify(value));
    } catch {
      // A registration must not be sent unless its exact retry is recoverable.
      if (pending.registrationRequest || pending.creationRequest)
        throw new Error(
          "Allow browser storage to create your account safely, then try again."
        );
    }
    this.memory.set(identity, pending);
  }
  delete(identity: string): void {
    this.memory.delete(identity);
    try {
      this.storage()?.removeItem(this.key(identity));
    } catch {
      /* Read-only storage. */
    }
  }
}

export const onboardingIdentity = (
  keys: V2Keys,
  context: V2KeyContext
): string =>
  JSON.stringify([
    context.chainId,
    context.proxyAddress,
    context.walletAddress.toLowerCase(),
    // An independently derived, hashed identity ID, not the spending scalar.
    keys.recoveryId,
  ]);

export class OnboardingConfirmationPendingError extends Error {
  constructor() {
    super(
      "Account confirmation is still pending. Try again to check its status; no new registration or account transaction will be sent."
    );
    this.name = "OnboardingConfirmationPendingError";
  }
}

type RecoveryMaterial = {
  recovery: { notes?: RecoveredNote[]; activeNotes: RecoveredNote[] };
};

export const hasPendingOnboardingNote = (
  current: RecoveryMaterial,
  pending: PendingOnboarding
): boolean => {
  const history = current.recovery.notes ?? current.recovery.activeNotes;
  const expected = history.some(
    ({ note }) =>
      note.kind === "control" &&
      note.commitment === pending.commitment &&
      (pending.kind === "registration"
        ? note.subwallet === null
        : note.subwallet === pending.subwallet)
  );
  if (!expected) return false;
  // The creation note may already have been consumed by another browser.
  // Recover its current successor rather than reusing a spent control note.
  return (
    pending.kind === "registration" ||
    current.recovery.activeNotes.some(
      ({ note }) =>
        note.kind === "control" && note.subwallet === pending.subwallet
    )
  );
};

/** Bounded, read-only recovery. Never manufactures a note or retries submission. */
export const waitForOnboardingNote = async <
  T extends RecoveryMaterial,
>(params: {
  pending: PendingOnboarding;
  initial?: T;
  recover: () => Promise<T>;
  assertCurrent: () => void;
  timeoutMs?: number;
  intervalMs?: number;
}): Promise<T> => {
  const timeoutMs = params.timeoutMs ?? 20_000;
  const intervalMs = params.intervalMs ?? 1_000;
  const deadline = Date.now() + timeoutMs;
  let current = params.initial;
  for (;;) {
    params.assertCurrent();
    if (current && hasPendingOnboardingNote(current, params.pending))
      return current;
    if (Date.now() >= deadline) throw new OnboardingConfirmationPendingError();
    if (current) {
      await new Promise<void>((resolve) =>
        setTimeout(resolve, Math.min(intervalMs, deadline - Date.now()))
      );
      params.assertCurrent();
    }
    const remaining = deadline - Date.now();
    if (remaining <= 0) throw new OnboardingConfirmationPendingError();
    let timer: ReturnType<typeof setTimeout> | undefined;
    try {
      current = await Promise.race([
        params.recover(),
        new Promise<never>((_resolve, reject) => {
          timer = setTimeout(
            () => reject(new OnboardingConfirmationPendingError()),
            remaining
          );
        }),
      ]);
      params.assertCurrent();
    } finally {
      if (timer !== undefined) clearTimeout(timer);
    }
  }
};
